import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Scissors, Share2, Loader2, PlayCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiError } from "@/lib/api";
import { LogoFull } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { RefBanner } from "@/components/referral/RefBanner";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="cx-label">{label}</span>
    <input data-testid={testid} {...props}
      className="mt-2 w-full rounded-lg bg-[#070912] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#2f7bff] focus:shadow-[0_0_0_3px_rgba(47,123,255,0.18)] transition-colors" />
  </label>
);

const readRef = () => {
  try {
    const r = JSON.parse(localStorage.getItem("clipx_ref") || "null");
    return r && Date.now() - r.at < 30 * 864e5 ? r.sid : undefined;
  } catch { return undefined; }
};

const Consent = ({ checked, onChange, t }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="register-consent-box">
    <label className="flex gap-3 items-start cursor-pointer">
      <input type="checkbox" data-testid="register-consent-checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} required
        className="mt-0.5 w-4 h-4 shrink-0 accent-[#2f7bff] cursor-pointer" />
      <span className="text-xs text-slate-300 leading-relaxed">
        {t("auth.consent_1")}
        <Link to="/conditions" target="_blank" className="text-[#3ad7ff] underline underline-offset-2" data-testid="register-terms-link">{t("auth.consent_terms")}</Link>
        {t("auth.consent_and")}
        <Link to="/confidentialite" target="_blank" className="text-[#3ad7ff] underline underline-offset-2" data-testid="register-privacy-link">{t("auth.consent_privacy")}</Link>
        {t("auth.consent_2")}
      </span>
    </label>
    <p className="text-[11px] text-slate-500 mt-2 pl-7">{t("auth.consent_note")}</p>
  </div>
);

const Perk = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4">
    <div className="w-11 h-11 shrink-0 rounded-lg grid place-items-center border border-[#2f7bff]/40 bg-[#2f7bff]/10 text-[#3ad7ff]"><Icon className="w-5 h-5" /></div>
    <div>
      <div className="font-display font-semibold text-white">{title}</div>
      <div className="text-sm text-slate-400">{text}</div>
    </div>
  </div>
);

export default function AuthPage() {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login"));
  const [f, setF] = useState({ workspace_id: "", workspace_name: "", name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accept, setAccept] = useState(false);
  const [termsVersion, setTermsVersion] = useState("");
  useEffect(() => { api.get("/version").then((r) => setTermsVersion(r.data.terms_version)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (fn, msg) => {
    setLoading(true);
    setError("");
    try {
      await fn();
      toast.success(msg);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (mode === "login") run(() => login({ workspace_id: f.workspace_id, email: f.email, password: f.password }), t("auth.welcome_back"));
    else if (!accept) setError(t("auth.consent_required"));
    else run(async () => {
      const u = await register({ accept_terms: true, terms_version: termsVersion, ref: readRef(), workspace_name: f.workspace_name, workspace_id: f.workspace_id || undefined, name: f.name, email: f.email, password: f.password });
      localStorage.removeItem("clipx_ref");
      if (u.ref_bonus) toast.success(t("ref.welcome_bonus").replace("{n}", u.ref_bonus), { duration: 6000 });
    }, t("auth.ws_created"));
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#04050a]"><div className="flex-1 grid lg:grid-cols-[1.2fr_1fr]">
      <section className="relative hidden lg:flex flex-col justify-center gap-10 px-14 xl:px-20 py-12 overflow-hidden border-r border-white/5">
        <img src="https://images.unsplash.com/photo-1542751371-adc38448a05e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#04050a]/75" />
        <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-[#2f7bff]/20 blur-[140px]" />
        <div className="absolute right-0 bottom-0 w-[420px] h-[420px] rounded-full bg-[#a746ff]/20 blur-[140px]" />
        <div className="relative grid xl:grid-cols-[auto_1fr] items-center gap-10 cx-rise">
          <LogoFull className="w-72 h-48 xl:w-96 xl:h-64" testid="auth-hero-logo" />
          <div>
            <div className="cx-label">{t("auth.engine")}</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl xl:text-6xl leading-[1.02] mt-4">
              <span className="cx-chrome">{t("auth.hero1")}</span><br /><span className="cx-text-grad">{t("auth.hero2")}</span>
            </h1>
            <p className="text-slate-400 mt-6 text-base max-w-lg">{t("auth.hero_sub")}</p>
          </div>
        </div>
        <div className="relative grid xl:grid-cols-3 gap-6 cx-rise" style={{ animationDelay: "0.1s" }}>
          <Perk icon={Zap} title={t("auth.perk1_t")} text={t("auth.perk1_d")} />
          <Perk icon={Scissors} title={t("auth.perk2_t")} text={t("auth.perk2_d")} />
          <Perk icon={Share2} title={t("auth.perk3_t")} text={t("auth.perk3_d")} />
        </div>
      </section>

      <section className="relative flex items-center justify-center p-5 sm:p-10">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-44"><LanguageSwitcher /></div>
        <div className="w-full max-w-md cx-rise pt-14 lg:pt-0">
          <LogoFull className="w-56 h-36 sm:w-72 sm:h-48 lg:hidden mx-auto mb-8" testid="auth-mobile-logo" />
          <div className="flex gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/5 mb-8">
            {[["login", t("auth.tab_login"), "login-tab-button"], ["register", t("auth.tab_register"), "register-tab-button"]].map(([m, l, id]) => (
              <button key={m} data-testid={id} onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${mode === m ? "cx-btn" : "text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          {mode === "register" && <RefBanner />}
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{mode === "login" ? t("auth.title_login") : t("auth.title_register")}</h2>
          <p className="text-slate-400 text-sm mt-2 mb-7">{mode === "login" ? t("auth.sub_login") : t("auth.sub_register")}</p>
          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && <Field label={t("auth.ws_name")} testid="register-workspace-name-input" placeholder="Team Nova Esports" value={f.workspace_name} onChange={set("workspace_name")} required />}
            <Field label={mode === "login" ? t("auth.ws_id") : t("auth.ws_id_opt")} testid="login-workspace-input" placeholder="team-nova" value={f.workspace_id} onChange={set("workspace_id")} required={mode === "login"} />
            {mode === "register" && <Field label={t("auth.nickname")} testid="register-name-input" placeholder="NovaClips" value={f.name} onChange={set("name")} required />}
            <Field label={t("auth.email")} testid="login-email-input" type="email" placeholder="toi@clip-ex.ca" value={f.email} onChange={set("email")} required />
            <Field label={t("auth.password")} testid="login-password-input" type="password" placeholder="••••••••" value={f.password} onChange={set("password")} required minLength={6} />
            {mode === "login" && <Link to="/forgot-password" className="block text-right text-xs text-[#3ad7ff] hover:text-white -mt-2" data-testid="forgot-password-link">{t("auth.forgot")}</Link>}
            {mode === "register" && <Consent checked={accept} onChange={setAccept} t={t} />}
            {error && <div data-testid="auth-error" className="text-sm text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-lg px-4 py-3">{error}</div>}
            <button type="submit" disabled={loading || (mode === "register" && !accept)} data-testid="login-submit-button" className="cx-btn w-full rounded-full py-3.5 font-semibold font-display tracking-wide flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? t("auth.submit_login") : t("auth.submit_register")}
            </button>
          </form>
          <div className="flex items-center gap-3 my-6 text-xs text-slate-600"><div className="h-px flex-1 bg-white/10" />{t("auth.or")}<div className="h-px flex-1 bg-white/10" /></div>
          {mode === "login" ? (
            <button data-testid="auth-try-register-button" onClick={() => { setMode("register"); setError(""); }} disabled={loading} className="cx-try-btn w-full rounded-full py-3 text-sm font-semibold text-white flex items-center justify-center gap-2">
              <PlayCircle className="w-4 h-4" /> {t("auth.try_cta")}
            </button>
          ) : (
            <div className="rounded-xl border border-[#ff4fd8]/40 bg-[#ff4fd8]/10 px-4 py-3 text-center text-sm text-[#ffc6f3]" data-testid="auth-register-perk">{t("auth.try_perk")}</div>
          )}
          <p className="text-center text-xs text-slate-500 mt-2">{t("auth.try_hint")}</p>
        </div>
      </section>
    </div>
    <Footer />
    </div>
  );
}
